import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { authService } from '@/services/auth.service';
import { getCurrentDomain } from '@/utils/domain';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import {
  extractErrorStatus,
  extractJsonApiErrorCode,
  extractJsonApiErrorMessage,
  extractJsonApiFieldErrors,
} from '@/lib/jsonapi/errors';

type FormValues = {
  email: string;
};

export default function CustomerForgotPassword() {
  useAuthPageTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('customer.dashboard.providerFallback');

  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled, isLoading: isAvailabilityLoading } =
    usePasswordLoginAvailability('customer', {
      tenantSlug: resolvedTenantSlug,
    });

  const redirectUrl = useMemo(
    () => (typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined),
    [],
  );

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('customer.portal.login.invalidEmail')),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      authService.requestCustomerPasswordReset({
        email: values.email,
        redirectUrl,
      }),
    onSuccess: () => {
      setErrorMessage(null);
      setStatusMessage(t('customer.portal.forgotPassword.success'));
      form.clearErrors();
    },
    onError: (error: unknown) => {
      setStatusMessage(null);
      const status = extractErrorStatus(error);
      const code = extractJsonApiErrorCode(error);
      const fieldErrors = extractJsonApiFieldErrors(error);

      Object.entries(fieldErrors).forEach(([field, message]) => {
        if (field === 'email') {
          form.setError('email', { message });
        }
      });

      let message = extractJsonApiErrorMessage(error, t('customer.portal.forgotPassword.error'));
      if (status === 423 || code === 'PASSWORD_LOGIN_DISABLED') {
        message = t('customer.portal.login.passwordLoginDisabled');
      }
      setErrorMessage(message);
    },
  });

  const handleSubmit = (values: FormValues) => {
    setErrorMessage(null);
    setStatusMessage(null);
    mutation.mutate(values);
  };

  const isFormDisabled =
    mutation.isPending || isAvailabilityLoading || !passwordLoginEnabled;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="mx-auto h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="mx-auto text-2xl font-semibold tracking-tight text-foreground">
              {brandName}
            </span>
          )}
          <CardTitle>{t('customer.portal.forgotPassword.title')}</CardTitle>
          <CardDescription>{t('customer.portal.forgotPassword.description')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {!passwordLoginEnabled && !isAvailabilityLoading ? (
            <Alert variant="destructive">
              <AlertDescription>{t('customer.portal.login.passwordLoginDisabled')}</AlertDescription>
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          {statusMessage ? (
            <Alert>
              <AlertDescription>{statusMessage}</AlertDescription>
            </Alert>
          ) : null}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.portal.login.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder={t('customer.portal.login.emailPlaceholder')}
                        disabled={isFormDisabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isFormDisabled}>
                {mutation.isPending
                  ? t('customer.portal.forgotPassword.submitting')
                  : t('customer.portal.forgotPassword.submit')}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate('/customer/login')}
          >
            {t('customer.portal.forgotPassword.backToLogin')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
